import { type RankedSchool, type Category, formatCents } from '../api';
import { SchoolAvatar } from './SchoolAvatar';
import { ladderProgress } from '../milestones';
import type { PledgeTarget } from './PledgePanel';

interface Props {
  category: Category | null;
  schools: RankedSchool[];
  conferenceName: (id: string) => string;
  onSelect: (target: PledgeTarget) => void;
}

export function Leaderboard({ category, schools, conferenceName, onSelect }: Props) {
  if (schools.length === 0) {
    return (
      <p className="empty">
        No schools ranked in {category?.name ?? 'this category'} yet. Find your school above and
        add the first points.
      </p>
    );
  }
  return (
    <ol className="board" aria-label={`${category?.name ?? ''} leaderboard`}>
      {schools.map((s) => {
        const { reached, next, ladderFraction, ticks } = ladderProgress(s.categoryCents);
        const pct = Math.round(ladderFraction * 100);
        const goalLabel = next
          ? `${formatCents(next.cents - s.categoryCents)} to ${next.label}`
          : 'Top tier reached';
        return (
          <li key={s.id} className={`board__row${s.rank <= 3 ? ' board__row--top' : ''}`}>
            <button
              className="board__click"
              onClick={() => onSelect(s)}
              aria-label={`Add points to ${s.name}`}
              style={{ borderLeft: `4px solid ${s.primary}` }}
            >
              <span className="board__rank" aria-label={`rank ${s.rank}`}>
                {s.rank}
              </span>
              <SchoolAvatar name={s.name} primary={s.primary} secondary={s.secondary} size={40} />
              <span className="board__main">
                <span className="board__name">
                  {s.name}
                  {reached && (
                    <span className="board__badge" title={`${reached.label}+ points`}>
                      {reached.medal} {reached.label}
                    </span>
                  )}
                </span>
                <span className="board__meta">
                  <span className="chip">{conferenceName(s.conferenceId)}</span>
                  <span className="chip chip--div">{s.division}</span>
                  <span className="board__contribs">
                    {s.contributorCount} {s.contributorCount === 1 ? 'contributor' : 'contributors'}
                  </span>
                </span>
              </span>
              <span className="board__total" title="Total contributed">
                {formatCents(s.categoryCents)}
              </span>
            </button>
            <div className="thermo-wrap">
              <div
                className="thermo"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Points ladder: ${goalLabel}`}
              >
                <div className="thermo__fill" style={{ width: `${pct}%`, background: s.primary }} />
                {ticks.map((t) => (
                  <span key={t.label} className="thermo__tick" style={{ left: `${t.positionPct}%` }}>
                    <span className="thermo__tick-line" />
                    <span className={`thermo__tick-label${t.reached ? ' is-reached' : ''}`}>
                      {t.medal} {t.label}
                    </span>
                  </span>
                ))}
              </div>
              <span className="thermo__caption">{goalLabel}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
